import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { matchPath, useLocation, useParams } from "react-router-dom";
import { getGroupList } from "../api/lib.api";
import { LibPathsEnum } from "../constants/lib.paths";
import { IOrganization, IUserRole } from "../models/lib.org";

interface OrgSidebarContextType {
  orgList: IOrganization[];
  targetId: number | undefined;
  targetLibraryId: number | undefined;
  fetchOrganizations: () => Promise<void>;
}

const OrgSidebarContext = createContext<OrgSidebarContextType | undefined>(
  undefined
);

interface OrgSidebarProviderProps {
  children: ReactNode;
}

export const OrgSidebarProvider: React.FC<OrgSidebarProviderProps> = ({
  children,
}) => {
  const { id } = useParams();
  const location = useLocation();
  const [orgList, setOrgList] = useState<IOrganization[]>([]);
  const [targetId, setTargetId] = useState<number | undefined>(undefined);
  const [targetLibraryId, setTargetLibraryId] = useState<number | undefined>(
    undefined
  );

  const fetchOrganizations = async () => {
    try {
      const result = await getGroupList();
      const ownOrg = result.data.filter(
        (org: IOrganization) => org.userRole === IUserRole.OWNER
      );
      setOrgList(ownOrg);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (orgList.length === 0) return;

    if (matchPath(LibPathsEnum.ORG_LIBRARY, location.pathname)) {
      const org = orgList.find((org) => org.id === Number(id));
      if (org) {
        setTargetId(org.id);
        setTargetLibraryId(org.libraryId);
        return;
      }
    } else if (matchPath(LibPathsEnum.GROUP_LIBRARY, location.pathname)) {
      const groups = orgList.map((org) => org.groups).flat();
      const group = groups.find((group) => group.id === Number(id));
      if (group) {
        setTargetId(group.id);
        setTargetLibraryId(group.libraryId || undefined);
        return;
      }
    }

    setTargetId(undefined);
    setTargetLibraryId(undefined);
  }, [location.pathname, orgList, id]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const value: OrgSidebarContextType = {
    orgList,
    targetId,
    targetLibraryId,
    fetchOrganizations,
  };

  return (
    <OrgSidebarContext.Provider value={value}>
      {children}
    </OrgSidebarContext.Provider>
  );
};

export const useOrgSidebar = (): OrgSidebarContextType => {
  const context = useContext(OrgSidebarContext);
  if (context === undefined) {
    throw new Error("useOrgSidebar must be used within an OrgSidebarProvider");
  }
  return context;
};
